// Base de données d'images médicales de référence
// Images provenant de DermNet NZ (dermnetnz.org) - Source médicale fiable
// URLs VÉRIFIÉES le 30/05/2026 - Toutes les URLs testées et fonctionnelles

export const MEDICAL_IMAGES = {
  // ============================================
  // DERMATOLOGIE - LESIONS ERYTHEMATO-SQUAMEUSES
  // ============================================
  psoriasis: {
    name: "Psoriasis en plaques",
    images: [
      {
        url: "https://dermnetnz.org/assets/Uploads/scaly/ps3.jpg",
        legend: "Psoriasis en plaques chronique - plaques bien limitées avec squames argentées",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/Uploads/scaly/ps6.jpg",
        legend: "Psoriasis du coude - localisation typique",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/Uploads/scaly/ps4.jpg",
        legend: "Psoriasis en gouttes",
        source: "DermNet NZ"
      }
    ]
  },

  // ============================================
  // INFECTIONS VIRALES
  // ============================================
  zona: {
    name: "Zona (Herpes Zoster)",
    images: [
      {
        url: "https://dermnetnz.org/assets/Uploads/viral/zoster-13.jpg",
        legend: "Zona thoracique - éruption vésiculeuse métamérique",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/Uploads/viral/zoster-14.jpg",
        legend: "Zona - vésicules groupées en bouquet sur trajet nerveux",
        source: "DermNet NZ"
      }
    ]
  },

  // ============================================
  // ECZEMAS ET DERMATITES
  // ============================================
  eczema: {
    name: "Eczéma / Dermatite atopique",
    images: [
      {
        url: "https://dermnetnz.org/assets/Uploads/SCORAD/atopic-dryness-3.jpg",
        legend: "Dermatite atopique sèche - pattern extenseur",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/Uploads/SCORAD/atopic-oozy-2.jpg",
        legend: "Dermatite atopique aiguë des plis",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/Uploads/atopic-eczema-02.jpg",
        legend: "Dermatite atopique chronique des plis",
        source: "DermNet NZ"
      }
    ]
  },

  // ============================================
  // URTICAIRE
  // ============================================
  urticaire: {
    name: "Urticaire",
    images: [
      {
        url: "https://dermnetnz.org/assets/Uploads/urticaria4.jpg",
        legend: "Urticaire - papules érythémateuses oedémateuses",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/Uploads/urticaria-200.jpg",
        legend: "Urticaire spontanée",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/Uploads/reactions/urticaria2.jpg",
        legend: "Urticaire - plaques urticariennes multiples",
        source: "DermNet NZ"
      }
    ]
  },

  // ============================================
  // INFECTIONS BACTERIENNES
  // ============================================
  impetigo: {
    name: "Impétigo",
    images: [
      {
        url: "https://dermnetnz.org/assets/collection/Impetigo/impetigo-0010.jpg",
        legend: "Impétigo - croûtes mélicériques typiques sur le menton",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/collection/Impetigo/impetigo-0009.jpg",
        legend: "Impétigo péri-oral - croûtes couleur miel",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/collection/Impetigo/impetigo-0002.jpg",
        legend: "Impétigo bulleux étendu",
        source: "DermNet NZ"
      }
    ]
  },

  // ============================================
  // ACNE ET ROSACEE
  // ============================================
  acne: {
    name: "Acné",
    images: [
      {
        url: "https://dermnetnz.org/assets/Uploads/acne/acne1.jpg",
        legend: "Acné vulgaire - papules et pustules inflammatoires",
        source: "DermNet NZ"
      },
      {
        url: "https://dermnetnz.org/assets/Uploads/acne/conglob2.jpg",
        legend: "Acné nodulokystique sévère",
        source: "DermNet NZ"
      }
    ]
  }
};

// Fonction pour trouver des images pertinentes basées sur le diagnostic principal
// ALGORITHME STRICT - Exactitude 99.99% requise pour application médicale
// Retourne UNIQUEMENT les images de la pathologie EXACTE recherchée
export const findRelevantImages = (userQuery) => {
  // Normaliser la requête utilisateur (pas le texte de réponse IA)
  const normalizedQuery = userQuery
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Supprimer accents pour comparaison
    .trim();
  
  // Configuration STRICTE des pathologies avec termes EXACTS et exclusions
  const pathologyConfig = {
    psoriasis: {
      // Termes qui DOIVENT être présents (au moins un)
      exactTerms: ['psoriasis'],
      // Termes qui EXCLUENT cette pathologie si présents
      exclusionTerms: ['dermatite', 'eczema', 'atopique'],
      // Priorité (plus haut = plus spécifique)
      priority: 100
    },
    zona: {
      exactTerms: ['zona', 'herpes zoster'],
      exclusionTerms: ['herpes labial', 'herpes genital', 'herpes simplex'],
      priority: 100
    },
    eczema: {
      exactTerms: ['eczema', 'dermatite atopique'],
      exclusionTerms: ['psoriasis', 'contact', 'seborrheique'],
      priority: 90
    },
    urticaire: {
      exactTerms: ['urticaire'],
      exclusionTerms: ['angioedeme seul'],
      priority: 100
    },
    impetigo: {
      exactTerms: ['impetigo'],
      exclusionTerms: ['ecthyma'],
      priority: 100
    },
    acne: {
      exactTerms: ['acne'],
      exclusionTerms: ['rosacee', 'folliculite'],
      priority: 100
    }
  };
  
  // Fonction pour vérifier si un terme EXACT est présent (mot complet)
  const hasExactTerm = (text, term) => {
    // Créer une regex pour matcher le mot complet
    const normalizedTerm = term
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    const regex = new RegExp(`(^|[\\s,;:.!?()\\[\\]{}])${normalizedTerm}([\\s,;:.!?()\\[\\]{}]|$)`, 'i');
    return regex.test(text);
  };
  
  // Trouver la pathologie EXACTE
  let matchedPathology = null;
  let highestPriority = -1;
  
  for (const [pathologyKey, config] of Object.entries(pathologyConfig)) {
    // Vérifier si un terme exact est présent
    const hasMatch = config.exactTerms.some(term => hasExactTerm(normalizedQuery, term));
    
    if (!hasMatch) continue;
    
    // Vérifier les exclusions
    const hasExclusion = config.exclusionTerms.some(term => hasExactTerm(normalizedQuery, term));
    
    if (hasExclusion) continue;
    
    // Si priorité plus haute, c'est le meilleur match
    if (config.priority > highestPriority && MEDICAL_IMAGES[pathologyKey]) {
      highestPriority = config.priority;
      matchedPathology = pathologyKey;
    }
  }
  
  // Retourner les images de la pathologie EXACTE trouvée
  if (matchedPathology && MEDICAL_IMAGES[matchedPathology]) {
    const pathologyData = MEDICAL_IMAGES[matchedPathology];
    return [{
      condition: matchedPathology,
      name: pathologyData.name,
      images: pathologyData.images.slice(0, 3) // Maximum 3 images
    }];
  }
  
  // Aucune correspondance exacte trouvée
  return [];
};

export default MEDICAL_IMAGES;
